// Reusable activity timeline. Used by property/customer detail pages and
// lead/appointment detail sheets. Takes `entityType` + `entityId` and
// renders a vertical list of activity log entries, with pagination
// ("Tải thêm") and skeleton/empty/error states.

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ActivityRow } from "@/features/activity/activity-row";
import { useActivities } from "@/features/activity/api";
import type { ActivityLogResponse } from "@/features/activity/api";

export interface ActivityTimelineProps {
  entityType: string;
  entityId: number;
  /** Items per page; defaults to 20 (the backend default). */
  pageSize?: number;
}

export function ActivityTimeline({
  entityType,
  entityId,
  pageSize = 20,
}: ActivityTimelineProps) {
  // The timeline is append-only: as the user pages forward we accumulate
  // rows. React Query's query key includes the page so each page caches
  // independently.
  const [page, setPage] = useState(0);
  const [accumulated, setAccumulated] = useState<ActivityLogResponse[]>([]);

  const query = useActivities({
    entityType,
    entityId,
    page,
    size: pageSize,
  });

  // Merge new pages into the accumulated list once each page resolves.
  // We dedupe by id so a refetch of page 0 doesn't double-render.
  if (query.data !== undefined) {
    const incoming = query.data.items;
    const known = new Set(accumulated.map((a) => a.id));
    const additions = incoming.filter((a) => !known.has(a.id));
    if (additions.length > 0) {
      // Functional state update outside an effect is fine here because
      // React Query data is referentially stable per page.
      setAccumulated((prev) => [...prev, ...additions]);
    }
  }

  if (query.isPending && accumulated.length === 0) {
    return (
      <div
        aria-busy="true"
        aria-label="Đang tải hoạt động"
        className="flex flex-col gap-3"
      >
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="size-8 shrink-0 rounded-full" />
            <div className="flex flex-1 flex-col gap-2 pt-1">
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-center text-sm text-muted-foreground">
        <p>Không thể tải lịch sử.</p>
        <Button
          variant="link"
          size="sm"
          onClick={() => void query.refetch()}
          className="mt-1"
        >
          Thử lại
        </Button>
      </div>
    );
  }

  if (accumulated.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-center text-sm text-muted-foreground">
        Chưa có hoạt động.
      </p>
    );
  }

  const totalPages = query.data?.totalPages ?? 0;
  const hasMore = page + 1 < totalPages;

  return (
    <div className="flex flex-col gap-2">
      <ol className="flex flex-col">
        {accumulated.map((item, idx) => (
          <ActivityRow
            key={item.id}
            item={item}
            isLast={idx === accumulated.length - 1 && !hasMore}
          />
        ))}
      </ol>
      {hasMore ? (
        <div className="flex justify-center pt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={query.isFetching}
          >
            {query.isFetching ? "Đang tải..." : "Tải thêm"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
